import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import styles from './Tabs.module.scss'
import PropTypes from 'prop-types'
import { Grid, Typography } from 'gu-ui-kit'
import clsx from 'clsx'
import useWindowSize from 'utils/hooks/useWindowSize'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

const Tabs = forwardRef(({ value, options = [], className = '', onChange = () => {} }, ref) => {
  const [bgVars, setBgVars] = useState({})
  const optionsRef = useRef({})
  const breakpoint = useBreakpoint()

  const calcVars = useCallback((target) => {
    const { width = 0 } = target?.getBoundingClientRect() ?? {}
    const left = target?.offsetLeft ?? 0

    setBgVars({
      '--bg-opacity': '1',
      '--bg-left': left + 'px',
      '--bg-width': width + 'px',
    })
  }, [])

  const handleChange = useCallback(
    (id) => {
      onChange(id)
    },
    [onChange],
  )

  const { width } = useWindowSize()

  useEffect(() => {
    if (!value || !optionsRef.current) return
    calcVars(optionsRef.current[value])
  }, [value, styles, optionsRef, width])

  return (
    <nav
      className={clsx(styles['tabs'], className)}
      ref={ref}
    >
      <Grid
        direction={'row'}
        component={'ul'}
        wrap={false}
        className={styles['tabs__list']}
      >
        {options.map(({ id, label }) => (
          <li
            ref={(el) => (optionsRef.current[id] = el)}
            key={id}
            className={styles['tabs__item']}
          >
            <Typography
              component={'button'}
              type={'button'}
              variant={!breakpoint.sm ? 'Body-15' : 'Body-20'}
              color={value === id ? 'Label-GU-Primary' : 'Label-Fifth'}
              className={clsx(styles['tabs__button'])}
              onClick={() => handleChange(id)}
            >
              {label}
            </Typography>
          </li>
        ))}
        <div
          className={styles['tabs__bg']}
          style={bgVars}
        />
      </Grid>
    </nav>
  )
})

Tabs.propTypes = {
  items: PropTypes.array,
  className: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.string,
  options: PropTypes.array,
}

Tabs.displayName = 'Tabs'

export default Tabs
